import { Model } from '@/models';

export interface NetworkStudySettings {
  details: {
    apiKey: string,
    apiKeyExpiration: number,
    archivedAt: string,
    avatar: string,
    b64Identifier: string,
    creation: string,
    description: string,
    identifier: string,
    longitudinal: boolean,
    organization: string,
    title: string,
    uri: string,
  }
  uri: string
}

interface IStudySettings {
  apiKey: string;
  apiKeyExpiration: number;
  archivedAt: string;
  avatar: string;
  identifier: string;
  longitudinal: boolean;
  title: string;
  uri: string;
}

export class StudySettingsModel extends Model {
  apiKey: string;
  apiKeyExpiration: number;
  archivedAt: string;
  avatar: string;
  identifier: string;
  longitudinal: boolean;
  title: string;
  uri: string;

  constructor({
    apiKey = '',
    apiKeyExpiration = 0,
    archivedAt = '',
    avatar = '',
    identifier = '',
    longitudinal = false,
    title = '',
    uri = ''
  }: Partial<IStudySettings> = {}) {
    super();
    this.apiKey = apiKey;
    this.apiKeyExpiration = apiKeyExpiration;
    this.archivedAt = archivedAt;
    this.avatar = avatar;
    this.identifier = identifier;
    this.longitudinal = longitudinal;
    this.title = title;
    this.uri = uri;
  }

  static mapFromBackend(networkStudySettings: NetworkStudySettings): StudySettingsModel {
    const { details } = networkStudySettings;
    return new StudySettingsModel({
      apiKey: details.apiKey,
      apiKeyExpiration: details.apiKeyExpiration,
      archivedAt: details.archivedAt,
      avatar: details.avatar,
      identifier: details.identifier,
      longitudinal: details.longitudinal,
      title: details.title,
      uri: details.uri
    });
  }
}

export interface WebhookSettings {
  RESPONSE_CREATED: {
    enabled: boolean;
    url: string;
  };
  RESPONSE_UPDATED: {
    enabled: boolean;
    url: string;
  };
  FORM_CHANGED: {
    enabled: boolean;
    url: string;
  };
}

export class WebhookModel extends Model {
  RESPONSE_CREATED: {
    enabled: boolean;
    url: string;
  };
  RESPONSE_UPDATED: {
    enabled: boolean;
    url: string;
  };
  FORM_CHANGED: {
    enabled: boolean;
    url: string;
  };

  constructor({
    RESPONSE_CREATED = { enabled: false, url: '' },
    RESPONSE_UPDATED = { enabled: false, url: '' },
    FORM_CHANGED = { enabled: false, url: '' }
  }: Partial<WebhookSettings> = {}) {
    super();
    this.RESPONSE_CREATED = RESPONSE_CREATED;
    this.RESPONSE_UPDATED = RESPONSE_UPDATED;
    this.FORM_CHANGED = FORM_CHANGED;
  }
}

export interface NetworkBillingSettings {
  activeInvestigations: string[];
  archivedInvestigations: string[];
  delinquent: boolean;
  manuallyBilled: boolean;
  features: object;
  freeTrial: boolean;
  organizationIdentifier: string;
  organizationOwner: string;
  organizationOwnerDetails: {
    avatars: string,
    createdAt: string,
    email: string,
    emailVerification: number,
    identifier: string,
    lastLogin: string,
    loginCount: number,
    names: string,
    phone: string,
    publicLinkData: string,
    staff: boolean,
    studyPagesIdentifier: string,
    updatedAt: string,
    userType: string,
    planIdentifier: string,
    provisionedUntil: string,
  }
  planIdentifier: string;
  provisionedUntil: string;
  quotas: BillingQuotas
}

interface IBillingSettings {
  manuallyBilled: boolean;
  activeInvestigations: string[];
  quotas: BillingQuotas;
  organizationOwner: string;
  organizationOwnerDetails: { names: string; email: string; };
  freeTrial: boolean;
}

interface OrganizationQuota {
  users: number;
  cases: number;
  responses: number;
  investigations: number;
  storage: number;
}

interface StudyQuota {
  users: number | null;
  cases: number;
  storage: number;
}

interface PlanQuota {
  cases: number;
  storage: number;
  users: number;
  participants: number;
  studies: number;
}

interface BillingQuotas {
  organization: OrganizationQuota;
  investigation: StudyQuota;
  plan: PlanQuota;
}

export class BillingModel extends Model {
  manuallyBilled: boolean;
  activeInvestigations: string[];
  quotas: BillingQuotas;
  organizationOwner: string;
  organizationOwnerDetails: { names: string; email: string; };
  freeTrial: boolean;

  constructor({
    manuallyBilled = false,
    activeInvestigations = [],
    quotas,
    organizationOwner,
    organizationOwnerDetails,
    freeTrial
  }: IBillingSettings) {
    super();
    this.manuallyBilled = manuallyBilled;
    this.activeInvestigations = activeInvestigations;
    this.quotas = quotas;
    this.organizationOwner = organizationOwner;
    this.organizationOwnerDetails = organizationOwnerDetails;
    this.freeTrial = freeTrial;
  }

  static mapFromBackend(billingSettings: NetworkBillingSettings): BillingModel {
    return new BillingModel({
      manuallyBilled: billingSettings.manuallyBilled,
      activeInvestigations: billingSettings.activeInvestigations,
      quotas: billingSettings.quotas,
      organizationOwner: billingSettings.organizationOwner,
      organizationOwnerDetails: billingSettings.organizationOwnerDetails,
      freeTrial: billingSettings.freeTrial
    });
  }
}


export interface StudySettingsUpdateData {
  identifier?: string;
  title?: string;
  type?: string;
  webhooks?: WebhookModel;
  avatar?: string;
  avatarType?: string;
  status?: string;
}
